import { useOverlayState } from "features/content-overlay"
import { get, has } from "nodash"
import { useCallback } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { TwoWayDecisionModalContent } from "themes/default-tailwind"
import { decidedAction, DecisionOption } from "./decisionSlice"
import {
    selectDecisionContextDescription,
    selectDecisionContextTitle,
    selectDecisionOptions,
} from "./decisionSelectors"

const minDecisionModalOptionsCount = 2

const getLabelFromDecisionOption = (option: DecisionOption) => {
    if (typeof option === "string" || typeof option === "number") {
        return option
    }

    const metaLabel = get(option, "meta.label")
    if (metaLabel) {
        return metaLabel
    }

    throw "getLabelFromDecisionOption encountered invalid option. Only string, number or valid Action with meta.label are supported."
}

const getActionFromOption = (option: DecisionOption) => {
    if (typeof option === "string" || typeof option === "number") {
        // broadcast the option chosen and let caller deal with it
        return { ...decidedAction(option), meta: { cleanup: true } }
    }

    // this would be a fully qualified action object
    if (has(option, "type") && has(option, "payload")) {
        return option
    }

    throw "getActionFromOption supports only string, number or redux Action objects"
}

/**
 * provides the logic to render the right modal content and dispatch updates after a decision has been made
 */
export const DecisionModalContainer = ({ closeModal }: { closeModal?: () => void }) => {
    const dispatch = useAppDispatch()
    const { hideOverlay } = useOverlayState()
    const title = useAppSelector(selectDecisionContextTitle)
    const description = useAppSelector(selectDecisionContextDescription)

    const options = useAppSelector(selectDecisionOptions)
    if (options.length < minDecisionModalOptionsCount) {
        throw "The DecisionModal must be initialized with at least two options"
    }

    const decisionHandler = useCallback(
        (option: DecisionOption) => () => {
            const action = getActionFromOption(option)
            const cleanupModalAfterDecision = Boolean(get(action, "meta.cleanup"))

            dispatch(action)
            if (cleanupModalAfterDecision) {
                hideOverlay()
            }
        },
        [dispatch, hideOverlay]
    )

    const firstOption = get(options, 0)
    const firstLabel = getLabelFromDecisionOption(firstOption)
    const secondOption = get(options, 1)
    const secondLabel = getLabelFromDecisionOption(secondOption)

    // @todo -- setup a size count checker and cleanup the inline label selection
    return (
        <TwoWayDecisionModalContent
            title={title}
            description={description}
            firstHandler={decisionHandler(firstOption)}
            firstLabel={firstLabel.toString()}
            secondHandler={decisionHandler(secondOption)}
            secondLabel={secondLabel.toString()}
            closeModal={closeModal}
        />
    )
}

export default DecisionModalContainer
